// ============================================
// src/App/levelsPullMerge.js
// MA-375 — the decision logic for App.jsx's cloud->local merge effect's
// math/reading LEVEL fields (the same once-per-session `cloudMergedStudentIdsRef`
// effect that cloudAssignedSkillsMerge.js feeds). Extracted to a plain,
// DI-friendly function (no React, no DOM) so the "which side wins" decision
// is directly unit-testable, mirroring cloudAssignedSkillsMerge.js's split.
//
// MA-39 built a server-side LWW guard (updateStudentLevelsSafe's PostgREST
// `.or(levels_updated_at.is.null,levels_updated_at.lte.<incoming>)` CAS) that
// protects the CLOUD from a stale device's push. Nothing protected the
// DEVICE from a stale cloud on the PULL side — the old inline merge in
// App.jsx preferred cloudStudent.math_level_id/reading_level_id unconditionally
// whenever present, so a parent's offline level override got silently
// clobbered back to the stale cloud value on the next boot. This comparator
// is the pull-side mirror of the push-side guard: local wins only when its
// levelsUpdatedAt stamp is strictly newer than the cloud's (or the cloud has
// no stamp at all, e.g. a pre-MA-39 row) — otherwise cloud wins, which is
// also the correct behavior for a genuinely-stale device re-hydrating.
//
// LANDMINE: this only works because levelsUpdatedAt is stamped by every live
// writer of mathModuleId/readingModuleId today (onboarding seeds both fields
// in lockstep with cloud-row creation; ParentSettingsSections.tsx's apply()
// stamps it explicitly). A future writer of these fields that forgets to
// also set levelsUpdatedAt silently defeats this guard — it would look like
// an infinitely-stale local device to every future comparison.
// ============================================

/**
 * @param {{
 *   localMathModuleId: string,
 *   localReadingModuleId: string,
 *   localLevelsUpdatedAt: string | null | undefined,
 *   cloudMathLevelId: string | null | undefined,
 *   cloudReadingLevelId: string | null | undefined,
 *   cloudLevelsUpdatedAt: string | null | undefined,
 *   fallbackMathModuleId: string,
 *   fallbackReadingModuleId: string,
 * }} args
 * @returns {{ mathModuleId: string, readingModuleId: string, levelsUpdatedAt: string | null, shouldPushLocal: boolean }}
 */
export function resolveLevelsPullMerge({
  localMathModuleId,
  localReadingModuleId,
  localLevelsUpdatedAt,
  cloudMathLevelId,
  cloudReadingLevelId,
  cloudLevelsUpdatedAt,
  fallbackMathModuleId,
  fallbackReadingModuleId,
}) {
  const localIsNewer =
    !!localLevelsUpdatedAt &&
    (!cloudLevelsUpdatedAt || localLevelsUpdatedAt > cloudLevelsUpdatedAt);

  if (localIsNewer) {
    return {
      mathModuleId: localMathModuleId || fallbackMathModuleId,
      readingModuleId: localReadingModuleId || fallbackReadingModuleId,
      levelsUpdatedAt: localLevelsUpdatedAt,
      shouldPushLocal: true,
    };
  }
  return {
    mathModuleId: cloudMathLevelId || localMathModuleId || fallbackMathModuleId,
    readingModuleId: cloudReadingLevelId || localReadingModuleId || fallbackReadingModuleId,
    levelsUpdatedAt: cloudLevelsUpdatedAt || localLevelsUpdatedAt || null,
    shouldPushLocal: false,
  };
}
